'use client';

import { useSession } from 'next-auth/react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
  saveProfileReaderSettingsReq,
  fetchProfileReaderSettingsReq,
} from '@/features/reader/api/reader-display-settings.requests';
import {
  readReaderSettingsCookie,
  writeReaderSettingsCookie,
} from '@/features/reader/components/reader-display-settings/utils/reader-display-settings.cookie';
import {
  DEFAULT_READER_DISPLAY_SETTINGS,
  resolveAuthenticatedReaderSettings,
  type ReaderDisplaySettings,
  type ReaderThemeId,
} from '@/features/reader/components/reader-display-settings/utils/reader-display-settings';

const SAVE_DEBOUNCE_MS = 350;

type ProfileWriteMode = 'unknown' | 'writable' | 'readonly';
type PersistTarget = 'cookie' | 'profile';

export function useReaderDisplaySettings() {
  const { data: session, status } = useSession();
  const accessToken = session?.accessToken;
  const userId = session?.user?.id;
  // Match the server render first. Cookie is applied after hydration so React
  // commits the theme change onto the existing DOM.
  const [settings, setSettings] = useState<ReaderDisplaySettings>(DEFAULT_READER_DISPLAY_SETTINGS);
  const settingsRef = useRef(settings);
  const dirtyRef = useRef(false);
  const profileModeRef = useRef<ProfileWriteMode>('unknown');
  const accessTokenRef = useRef(accessToken);
  const isAuthenticatedRef = useRef(false);
  const loadedIdentityRef = useRef<string | null>(null);
  const loadAttemptRef = useRef(0);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTargetRef = useRef<PersistTarget>('cookie');
  const pendingTokenRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    accessTokenRef.current = accessToken;
    isAuthenticatedRef.current = status === 'authenticated' && Boolean(accessToken);
  }, [accessToken, status]);

  useLayoutEffect(() => {
    const cookie = readReaderSettingsCookie();
    if (!cookie || dirtyRef.current) return;
    setSettings(cookie);
  }, []);

  const flushPendingSave = useCallback(() => {
    if (!saveTimerRef.current) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
    const next = settingsRef.current;
    if (pendingTargetRef.current === 'cookie') {
      writeReaderSettingsCookie(next);
      return;
    }
    const token = pendingTokenRef.current;
    if (token) {
      void saveProfileReaderSettingsReq(token, next);
    }
  }, []);

  const scheduleSave = useCallback((next: ReaderDisplaySettings) => {
    const useCookie = !isAuthenticatedRef.current || profileModeRef.current === 'readonly';
    pendingTargetRef.current = useCookie ? 'cookie' : 'profile';
    pendingTokenRef.current = accessTokenRef.current;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      if (pendingTargetRef.current === 'cookie') {
        writeReaderSettingsCookie(next);
        return;
      }
      const token = pendingTokenRef.current;
      if (token) {
        void saveProfileReaderSettingsReq(token, next);
      }
    }, SAVE_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      flushPendingSave();
    };
  }, [flushPendingSave]);

  useEffect(() => {
    if (status === 'loading') return;

    const identity = status === 'authenticated' && accessToken ? `user:${userId ?? ''}` : 'guest';
    const attempt = ++loadAttemptRef.current;
    let cancelled = false;

    if (loadedIdentityRef.current !== identity) {
      const previousIdentity = loadedIdentityRef.current;
      flushPendingSave();
      loadedIdentityRef.current = identity;
      dirtyRef.current = false;

      if (identity === 'guest') {
        profileModeRef.current = 'unknown';
        if (previousIdentity?.startsWith('user:')) {
          const cookie = readReaderSettingsCookie() ?? DEFAULT_READER_DISPLAY_SETTINGS;
          queueMicrotask(() => {
            if (cancelled || loadAttemptRef.current !== attempt || dirtyRef.current) return;
            setSettings(cookie);
          });
        }
      }
    }

    if (identity === 'guest' || !accessToken) {
      return () => {
        cancelled = true;
      };
    }

    const token = accessToken;
    void (async () => {
      const profile = await fetchProfileReaderSettingsReq(token);
      if (cancelled || loadAttemptRef.current !== attempt) return;

      if (profile.status === 'unavailable') {
        profileModeRef.current = 'readonly';
      } else {
        profileModeRef.current = 'writable';
      }

      if (dirtyRef.current) return;

      const cookie = readReaderSettingsCookie();
      const decision = resolveAuthenticatedReaderSettings({ profile, cookie });
      setSettings(decision.settings);

      if (decision.seed && !dirtyRef.current) {
        await saveProfileReaderSettingsReq(token, decision.seed);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accessToken, flushPendingSave, status, userId]);

  const applySettings = useCallback(
    (next: ReaderDisplaySettings) => {
      dirtyRef.current = true;
      setSettings(next);
      scheduleSave(next);
    },
    [scheduleSave],
  );

  const setTheme = useCallback(
    (theme: ReaderThemeId) => {
      applySettings({ ...settingsRef.current, theme });
    },
    [applySettings],
  );

  const setFontSize = useCallback(
    (fontSize: number) => {
      applySettings({ ...settingsRef.current, fontSize });
    },
    [applySettings],
  );

  const setLineHeight = useCallback(
    (lineHeight: number) => {
      applySettings({ ...settingsRef.current, lineHeight });
    },
    [applySettings],
  );

  const resetSettings = useCallback(() => {
    applySettings({
      ...DEFAULT_READER_DISPLAY_SETTINGS,
      extra: settingsRef.current.extra,
      version: settingsRef.current.version,
    });
  }, [applySettings]);

  return {
    settings,
    setTheme,
    setFontSize,
    setLineHeight,
    resetSettings,
  };
}
